import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingApi, unwrap } from '@/lib/api/client';
import type { BookingSearchModel } from '@/features/manage-bookings/bookings.api';
import type { UserSearchResult } from '@/features/manage-subscriptions/types';
import type { BookingDashboard, BookingTimeslot } from './types';

/** The aggregate half of `AdminBookingSearchModel` — same body, no paging. */
export function useBookingDashboard(model: BookingSearchModel) {
  return useQuery({
    queryKey: ['sales', 'dashboard', model],
    queryFn: () =>
      unwrap<BookingDashboard>(
        bookingApi.post('v2/admin/bookings/dashboard', model)
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * The timeslots of one event, filtered by the same search model as the rollup.
 *
 * Fetched only when a row is opened: the rollup deliberately carries no
 * timeslots, so one event costs one request rather than every event paying for
 * every booking up front.
 */
export function useEventTimeslots(
  eventId: string | null,
  model: BookingSearchModel
) {
  return useQuery({
    queryKey: ['sales', 'timeslots', eventId, model],
    queryFn: () =>
      unwrap<BookingTimeslot[]>(
        bookingApi.post(`v2/admin/bookings/events/${eventId}/timeslots`, model)
      ),
    enabled: !!eventId,
  });
}

/**
 * `CreateAdminBookingModel` — books a person into a timeslot on their behalf.
 *
 * `chargeNormally` is the whole decision: false books them in without money
 * changing hands, true puts it through the usual charge flow. The payment
 * fields are only sent when money actually changed hands, and the API books and
 * attaches the payment in one call, so this cannot half-succeed the way two
 * requests could.
 */
export interface CreateBookingInput {
  eventTimeSlotId: string;
  userId: string;
  comments: string;
  chargeNormally: boolean;
  /** Required by the server once the event has an active package. */
  eventPackageId?: string;
  amountReceived?: number;
  paymentMethod?: number;
  offlineReference?: string;
  adminNotes?: string;
}

export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateBookingInput) =>
      unwrap<unknown>(bookingApi.post('v2/admin/bookings', body)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['manage-bookings'] });
    },
  });
}

/** `POST admin/users/search` — who the booking is for. */
export function useUserSearch() {
  return useMutation({
    mutationFn: (searchTerm: string) =>
      unwrap<UserSearchResult[]>(
        bookingApi.post('admin/users/search', { searchTerm })
      ),
  });
}
